import { lazy } from "react";
import { Navigate, type RouteObject } from "react-router-dom";

// Layouts
import { DashboardAuthLayout } from "@/layouts/AuthLayout";
import DashboardLayout from "@/layouts/DashboardLayout";

import RequireRole from "./RequireRole";
import { type AllowedRoles, ROUTE_ROLES } from "./routeAccess";

// Lazy load the page components
const Dashboard = lazy(() => import("@/pages/DashboardPage"));
const NotificationsPage = lazy(() => import("@/pages/NotificationsPage"));
const ProfilePage = lazy(() => import("@/pages/ProfilePage"));
const FilesPage = lazy(() => import("@/pages/FilesPage"));
const CommentsPage = lazy(() => import("@/pages/CommentsPage"));

const Projects = lazy(() => import("@/pages/ProjectsPage"));
const ProjectServiceInspectionPage = lazy(() => import("@/pages/ProjectDetailsPage"));

const UsersManagementPage = lazy(() => import("@/pages/UsersManagementPage"));

const SettingsPage = lazy(() => import("@/pages/SettingsPage"));

/*
 * Dashboard pages open to `roles`. The role check runs before the dashboard
 * layout, so a user without access gets exactly the Not Found page an unknown
 * URL gets. Every group has the same RequireRole -> DashboardLayout shape, so
 * the layout stays mounted while navigating between groups.
 */
const dashboardPages = (
  roles: AllowedRoles,
  children: RouteObject[],
): RouteObject => ({
  element: <RequireRole roles={roles} />,
  children: [{ element: <DashboardLayout />, children }],
});

/*
 * Every dashboard page is an explicit route, so the URL alone decides what
 * renders. Restricted pages use the same role lists as the sidebar.
 */
const PrivateRoutes: RouteObject[] = [
  {
    element: <DashboardAuthLayout />,
    children: [
      dashboardPages("ANY", [
        { path: "dashboard", element: <Dashboard /> },
        { path: "notifications", element: <NotificationsPage /> },
        { path: "profile", element: <ProfilePage /> },
        {
          path: "projects",
          children: [
            { index: true, element: <Projects /> },
            // GEOV-247: the create-project wizard is gone (projects are
            // created from the New project dialog). Redirect old links and
            // bookmarks; without this, "create" would match :projectId.
            { path: "create", element: <Navigate to="/projects" replace /> },
            { path: ":projectId", element: <ProjectServiceInspectionPage /> },
          ],
        },
      ]),
      dashboardPages(ROUTE_ROLES.users, [
        { path: "users", element: <UsersManagementPage /> },
        { path: "users-management", element: <UsersManagementPage /> },
      ]),
      dashboardPages(ROUTE_ROLES.settings, [
        { path: "settings", element: <SettingsPage /> },
      ]),
      dashboardPages(ROUTE_ROLES.files, [
        { path: "files", element: <FilesPage /> },
      ]),
      dashboardPages(ROUTE_ROLES.comments, [
        { path: "comments", element: <CommentsPage /> },
      ]),
      dashboardPages(ROUTE_ROLES.myProjects, [
        { path: "my-projects", element: <Projects /> },
      ]),
    ],
  },
];

export default PrivateRoutes;
